import { useState, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { DATENSCHUTZ_TEXT } from "@/lib/constants";
import { RichContent } from "@/components/RichTextEditor";

export default function FormRenderer({ courseId, fields, onSubmit, submitting, consents = [{ key: "psb1", label: "Erziehungsberechtigte(r)" }], reference = null, submitLabel = "Anmeldung absenden", skipRequired = false, dsTemplates = [] }) {
  const [values, setValues] = useState({});
  const [kindName, setKindName] = useState("");
  const [uploading, setUploading] = useState({});
  const [uploads, setUploads] = useState({});
  const [page, setPage] = useState(0);
  const [cons, setCons] = useState({});
  const setC = (k, patch) => setCons((s) => ({ ...s, [k]: { ...(s[k] || {}), ...patch } }));

  const pages = useMemo(() => {
    const res = [[]];
    (fields || []).forEach((f) => {
      if (f.type === "pagebreak") res.push([]);
      else res[res.length - 1].push(f);
    });
    return res;
  }, [fields]);
  const lastPage = page >= pages.length - 1;
  const gidx = (f) => fields.indexOf(f);
  const dsMap = useMemo(() => Object.fromEntries((dsTemplates || []).map((t) => [t.id, t])), [dsTemplates]);
  const hasDsField = useMemo(() => (fields || []).some((f) => f.type === "datenschutz"), [fields]);
  const dsFieldsOf = (f) => (dsMap[f.template_id]?.fields || []);
  const expand = (list) => (list || []).flatMap((f) => (f.type === "datenschutz" ? dsFieldsOf(f) : [f]));

  const renderConsents = () => consents.map((c) => (
    <div key={c.key} className="border-t border-border pt-3 space-y-1" data-testid={`consent-${c.key}`}>
      <Label>Name {c.label} <span className="text-destructive">*</span></Label>
      <Input value={(cons[c.key] || {}).name || ""} onChange={(e) => setC(c.key, { name: e.target.value })} placeholder="Vor- und Nachname" data-testid={`consent-name-${c.key}`} />
      <label className="flex items-start gap-2 cursor-pointer pt-1">
        <Checkbox checked={!!(cons[c.key] || {}).accepted} onCheckedChange={(v) => setC(c.key, { accepted: !!v })} data-testid={`consent-accept-${c.key}`} className="mt-0.5" />
        <span className="text-sm">Ich stimme als {c.label} der Datenschutzerklärung zu.</span>
      </label>
    </div>
  ));

  const set = (id, v) => setValues((s) => ({ ...s, [id]: v }));
  const isVisible = (f) => {
    const conds = (f.visible_conditions || (f.visible_if ? [f.visible_if] : [])).filter((c) => c && c.field);
    if (conds.length === 0) return true;
    const test = (c) => { const cv = values[c.field]; return Array.isArray(cv) ? cv.includes(c.value) : cv === c.value; };
    return f.visible_mode === "or" ? conds.some(test) : conds.every(test);
  };
  const removeFile = (id) => {
    setValues((s) => { const n = { ...s }; delete n[id]; return n; });
    setUploads((u) => { const n = { ...u }; delete n[id]; return n; });
  };
  const toggleCheckbox = (id, opt) => {
    const cur = values[id] || [];
    set(id, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  const uploadFile = async (id, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [id]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/courses/${courseId}/user-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(id, data.filename);
      setUploads((u) => ({ ...u, [id]: data.file_id }));
      toast.success(`Datei hochgeladen: ${data.filename}`);
    } catch {
      toast.error("Upload fehlgeschlagen.");
    } finally {
      setUploading((u) => ({ ...u, [id]: false }));
    }
  };

  const validateFields = (list) => {
    for (const f of list) {
      if (f.type === "heading" || f.type === "pagebreak" || f.type === "datenschutz") continue;
      if (!isVisible(f)) continue;
      if (f.required && !skipRequired) {
        const v = values[f.id];
        if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
          toast.error(`Bitte Pflichtfeld ausfüllen: ${f.label}`);
          return false;
        }
      }
      if (f.type === "select" && f.allow_other && values[f.id] === "__sonstiges__" && !values[`${f.id}__other`]) {
        toast.error(`Bitte „Sonstiges" ausfüllen: ${f.label}`);
        return false;
      }
    }
    return true;
  };

  const isPageEmpty = (i) => i > 0 && i < pages.length - 1 && pages[i].length > 0 && pages[i].every((f) => !isVisible(f));
  const goNext = () => {
    if (!validateFields(expand(pages[page]))) return;
    let next = page + 1;
    while (next < pages.length - 1 && isPageEmpty(next)) next++;
    setPage(Math.min(next, pages.length - 1));
  };
  const goBack = () => {
    let prev = page - 1;
    while (prev > 0 && isPageEmpty(prev)) prev--;
    setPage(Math.max(prev, 0));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    for (const pg of pages) if (!validateFields(expand(pg))) return;
    if (!skipRequired) {
      for (const c of consents) {
        const d = cons[c.key] || {};
        if (!(d.name || "").trim()) { toast.error(`Bitte Namen angeben: ${c.label}`); return; }
        if (!d.accepted) { toast.error(`Bitte zustimmen: ${c.label}`); return; }
      }
    }
    const finalAnswers = { ...values };
    expand(fields).forEach((f) => {
      if (f.type === "select" && f.allow_other && values[f.id] === "__sonstiges__") {
        finalAnswers[f.id] = values[`${f.id}__other`] || "";
      }
      delete finalAnswers[`${f.id}__other`];
      if (!isVisible(f)) delete finalAnswers[f.id];
    });
    const consentsOut = {};
    consents.forEach((c) => { const d = cons[c.key] || {}; consentsOut[c.key] = { name: d.name.trim(), accepted: true, timestamp: new Date().toISOString() }; });
    onSubmit({ kind_name: kindName, answers: finalAnswers, uploads, consents: consentsOut }, () => {
      setValues({}); setKindName(""); setPage(0); setUploads({}); setCons({});
    });
  };

  const renderField = (f, keyIdx) => {
    const idx = keyIdx !== undefined ? keyIdx : gidx(f);
    if (f.type === "datenschutz") {
      if (!isVisible(f)) return null;
      const tpl = dsMap[f.template_id];
      const tplFields = tpl?.fields || [];
      return (
        <div key={f.id} className="border border-border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="ds-consent-block">
          <p className="font-semibold text-sm">Datenschutzerklärung</p>
          {(tpl?.text || !tpl) && (
            <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-white p-3 text-xs leading-relaxed" data-testid="ds-consent-text">
              {tpl ? <RichContent html={tpl.text} className="text-xs" /> : <span className="whitespace-pre-line">{DATENSCHUTZ_TEXT}</span>}
            </div>
          )}
          {tplFields.length > 0 && (
            <div className="space-y-4 rounded-md border border-border bg-white p-3" data-testid={`ds-fields-${idx}`}>
              {tplFields.map((tf, i) => renderField(tf, `${idx}-${i}`))}
            </div>
          )}
          {tpl?.text_end && (
            <div className="max-h-56 overflow-y-auto rounded-md border border-border bg-white p-3 text-xs leading-relaxed" data-testid={`ds-consent-text-end-${idx}`}>
              <RichContent html={tpl.text_end} className="text-xs" />
            </div>
          )}
          {renderConsents()}
        </div>
      );
    }
    if (f.type === "heading")
      return isVisible(f) ? <p key={f.id} className="font-bold text-base pt-3 pb-1 border-b border-border" data-testid={`reg-heading-${idx}`}>{f.label}</p> : null;
    if (!isVisible(f)) return null;
    return (
      <div key={f.id} className="space-y-2" data-testid={`reg-field-${idx}`}>
        <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>

        {f.type === "short" && <Input value={values[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} placeholder={f.note || ""} data-testid={`reg-input-${idx}`} />}
        {f.type === "long" && <Textarea value={values[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} placeholder={f.note || ""} data-testid={`reg-input-${idx}`} />}
        {f.type === "date" && <Input type="text" inputMode="numeric" placeholder="TT.MM.JJJJ" value={values[f.id] || ""} onFocus={(e) => { e.target.type = "date"; }} onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }} onChange={(e) => set(f.id, e.target.value)} data-testid={`reg-input-${idx}`} />}
        {f.type === "time" && <Input type="time" value={values[f.id] || ""} onChange={(e) => set(f.id, e.target.value)} data-testid={`reg-input-${idx}`} />}

        {f.type === "file" && (
          <div>
            <Input key={`${f.id}-${values[f.id] || "empty"}`} type="file" onChange={(e) => uploadFile(f.id, e.target.files[0])} data-testid={`reg-input-${idx}`} />
            {uploading[f.id] && <p className="text-xs text-muted-foreground mt-1">Wird hochgeladen…</p>}
            {values[f.id] && (
              <p className="text-xs text-green-700 mt-1 flex items-center gap-2">
                <span>✓ {values[f.id]}</span>
                <button type="button" onClick={() => removeFile(f.id)} className="text-destructive hover:underline" data-testid={`reg-file-remove-${idx}`}>Entfernen</button>
              </p>
            )}
          </div>
        )}

        {f.type === "select" && (
          <div className="space-y-2">
            <Select value={values[f.id] || ""} onValueChange={(v) => set(f.id, v)}>
              <SelectTrigger data-testid={`reg-input-${idx}`}><SelectValue placeholder={f.note || "Bitte wählen"} /></SelectTrigger>
              <SelectContent>
                {(f.options || []).filter(Boolean).map((o, i) => <SelectItem key={i} value={o}>{o}</SelectItem>)}
                {f.allow_other && <SelectItem value="__sonstiges__">Sonstiges</SelectItem>}
              </SelectContent>
            </Select>
            {f.allow_other && values[f.id] === "__sonstiges__" && (
              <Input value={values[`${f.id}__other`] || ""} onChange={(e) => set(`${f.id}__other`, e.target.value)} placeholder="Bitte angeben" data-testid={`reg-input-${idx}-other`} />
            )}
          </div>
        )}

        {f.type === "radio" && (
          <RadioGroup value={values[f.id] || ""} onValueChange={(v) => set(f.id, v)} data-testid={`reg-input-${idx}`}>
            {(f.options || []).filter(Boolean).map((o, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value={o} id={`${f.id}-${i}`} /> <span className="text-sm">{o}</span>
              </label>
            ))}
          </RadioGroup>
        )}

        {f.type === "checkbox" && (
          <div className="space-y-2" data-testid={`reg-input-${idx}`}>
            {(f.options || []).filter(Boolean).map((o, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={(values[f.id] || []).includes(o)} onCheckedChange={() => toggleCheckbox(f.id, o)} /> <span className="text-sm">{o}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl" data-testid="registration-form">
      {page === 0 && (
        <div className="space-y-2">
          <Label>Name des Kindes (zur Übersicht)</Label>
          <Input value={kindName} onChange={(e) => setKindName(e.target.value)} placeholder="Vor- und Nachname" data-testid="reg-kind-name" />
        </div>
      )}
      {pages.length > 1 && <p className="text-sm font-medium text-primary" data-testid="reg-page-indicator">Seite {page + 1} von {pages.length}</p>}

      {pages[page].map(renderField)}

      {lastPage && (
        <div className="space-y-4">
          {reference && (
            <div className="border border-border rounded-lg p-4 bg-blue-50/50" data-testid="cosign-reference">
              <p className="font-semibold text-sm mb-2">{reference.title || "Bereits gemachte Angaben"}</p>
              <div className="space-y-1 text-sm">
                {(reference.fields || []).filter((f) => f.type !== "heading" && f.type !== "pagebreak").map((f) => (
                  <div key={f.id} className="flex gap-2"><span className="text-muted-foreground">{f.label}:</span><span>{Array.isArray(reference.answers?.[f.id]) ? reference.answers[f.id].join(", ") : (reference.answers?.[f.id] || "—")}</span></div>
                ))}
              </div>
            </div>
          )}
          {!hasDsField && (
          <div className="border border-border rounded-lg p-4 bg-slate-50 space-y-3" data-testid="ds-consent-block">
            <p className="font-semibold text-sm">Datenschutzerklärung</p>
            <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-white p-3 text-xs whitespace-pre-line leading-relaxed" data-testid="ds-consent-text">{DATENSCHUTZ_TEXT}</div>
            {renderConsents()}
          </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        {page > 0 && <Button type="button" variant="outline" onClick={goBack} data-testid="reg-back-button">Zurück</Button>}
        {!lastPage && <Button type="button" onClick={goNext} data-testid="reg-next-button">Weiter</Button>}
        {lastPage && (
          <Button type="submit" disabled={submitting} data-testid="submit-registration-button">
            {submitting ? "Wird gesendet…" : submitLabel}
          </Button>
        )}
      </div>
    </form>
  );
}
